import type { CityConfig } from "@/pages/city-template/types";

export const milwaukeeConfig: CityConfig = {
  city: "Milwaukee",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "milwaukee",
  route: "/cities/milwaukee",
  title: "Emergency & Expedited Passport Services Milwaukee WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Milwaukee, WI — as fast as 24 hours. Wisconsin has no local passport agency. Fast Passport Center couriers your documents to Chicago. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Milwaukee, WI",
  heroSubtitle: "Milwaukee and southeastern Wisconsin have no local passport agency — the nearest is Chicago, just 90 miles south. Fast Passport Center's registered couriers deliver your documents directly to the Chicago Passport Agency, getting you a new or renewed passport as fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Milwaukee%20Wisconsin%20skyline%20at%20dusk%20with%20the%20lakefront%20harbor%20and%20Lake%20Michigan%20in%20the%20foreground%2C%20city%20lights%20reflecting%20on%20calm%20water%2C%20modern%20architecture%20with%20historic%20brick%20buildings%2C%20warm%20amber%20and%20deep%20blue%20tones%2C%20professional%20cityscape%20photography%2C%20wide%20angle%20view%2C%20clean%20composition%20with%20no%20text%20or%20people&width=1800&height=700&seq=milwaukee-hero-001&orientation=landscape",
  heroImageAlt: "Milwaukee Wisconsin skyline with Lake Michigan waterfront at dusk",
  geoPosition: "43.0389;-87.9065",
  geoPlacename: "Milwaukee, Wisconsin",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 2500, Chicago, IL 60604",
    distance: "Approximately 90 miles from Milwaukee — about 1.5 hours via I-94",
  },
  nearbyCitiesZips: [
    { name: "Downtown Milwaukee", zips: "53202, 53203, 53204" },
    { name: "Bay View", zips: "53207" },
    { name: "Wauwatosa", zips: "53213, 53226" },
    { name: "West Allis", zips: "53214, 53219" },
    { name: "Greenfield", zips: "53220, 53221" },
    { name: "Oak Creek", zips: "53154" },
    { name: "Franklin", zips: "53132" },
    { name: "South Milwaukee", zips: "53172" },
    { name: "Cudahy", zips: "53110" },
    { name: "Brookfield", zips: "53005, 53045" },
    { name: "Waukesha", zips: "53186, 53188" },
    { name: "New Berlin", zips: "53146, 53151" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission direct to Chicago Passport Agency" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Milwaukee case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS location in Milwaukee" },
  ],
  internalLinks: [
    { name: "Expedited passport in Madison WI", route: "/cities/madison-wi" },
    { name: "Emergency passport in Green Bay WI", route: "/cities/green-bay" },
    { name: "Urgent passport services in Racine WI", route: "/cities/racine" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago Passport Agency services", route: "/states/illinois" },
    { name: "Same-day passport help in Minneapolis", route: "/cities/minneapolis" },
    { name: "Expedited passport in Indianapolis", route: "/cities/indianapolis" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Milwaukee remotely via secure courier. Our registered couriers hand-deliver your documents directly to the Chicago Passport Agency — just 90 miles south of Milwaukee. This is how we achieve 24–48 hour turnaround for Milwaukee travelers without requiring you to make the drive to Chicago.",
  fpcOfficeDistance: "Serving all of Milwaukee County and southeastern Wisconsin",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire Milwaukee metro area. From downtown Milwaukee to Wauwatosa, from Brookfield to South Milwaukee — we help southeastern Wisconsin travelers get their passports fast, often in 24–48 hours through our registered Chicago courier service.",
  nearbyCitiesIntro: "We serve all Milwaukee neighborhoods and nearby communities including Wauwatosa, West Allis, Greenfield, Brookfield, Waukesha, Oak Creek, and South Milwaukee. If you are in Milwaukee County or Waukesha County, we have you covered.",
  howItWorksIntro: "Most Milwaukee travelers complete everything online and ship documents overnight. No need to drive 90 miles to Chicago or wait for a passport agency appointment. Our emergency and expedited passport process is designed for Wisconsin travelers who need fast, reliable results.",
  mistakesIntro: "With MKE Airport serving international routes and Milwaukee travelers often planning cruises from Chicago, getting passport delays wrong is costly. Here are the most common mistakes we see.",
  testimonialsIntro: "Milwaukee travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Milwaukee? Here are the most common ones we hear from Wisconsin travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Drive 90 miles to Chicago Passport Agency",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Chicago Agency hand-delivery — no drive required",
        "Predictable turnaround for Milwaukee travelers",
        "Ideal for upcoming trips from MKE Airport",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned international travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Milwaukee travelers with flights within 72 hours — our couriers hand-deliver to Chicago Passport Agency in as fast as 24 hours.",
      bullets: ["24–48 hour turnaround available", "Chicago Agency courier hand-delivery", "No drive to Chicago required"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Milwaukee adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support across Milwaukee County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement with DS-64 form guidance and identity verification support for Milwaukee residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support across southeastern Wisconsin.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name changes for Milwaukee residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Milwaukee", description: "Urgent passport processing for Milwaukee travelers with immediate departure needs via Chicago Passport Agency courier." },
    { name: "Passport Renewal Milwaukee", description: "Fast passport renewal for Milwaukee, WI residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport Application Milwaukee", description: "First-time passport application assistance for Milwaukee and southeastern Wisconsin residents." },
    { name: "Lost or Stolen Passport Milwaukee", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance for Milwaukee residents." },
    { name: "Child Passport Milwaukee", description: "Passport services for minors under 16 in Milwaukee, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change Passport Milwaukee", description: "Passport name change service after marriage, divorce, or legal name changes for Milwaukee residents." },
  ],
  testimonials: [
    {
      name: "Jennifer W.",
      location: "Wauwatosa, WI",
      rating: 5,
      text: "My passport expired two weeks before our Europe trip. I found out Wisconsin has no passport agency and panicked. Fast Passport Center couriered to Chicago and got me my renewed passport in 4 days. Absolutely incredible.",
      service: "Expedited Renewal",
    },
    {
      name: "Marcus D.",
      location: "Bay View, Milwaukee",
      rating: 5,
      text: "Business trip to Mexico City in 48 hours — my passport was expired. I called Fast Passport Center at 8 AM and had a new passport by the next afternoon. They drove to Chicago so I didn't have to.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah T.",
      location: "Brookfield, WI",
      rating: 5,
      text: "Getting my kids' passports was so much easier with their guidance. They explained exactly what we needed for both parents to appear. All three passports processed in a week.",
      service: "Child Passport",
    },
    {
      name: "Kevin L.",
      location: "West Allis, WI",
      rating: 5,
      text: "Lost my passport before a Caribbean cruise sailing from Chicago. Fast Passport Center handled the DS-64, coordinated with the Chicago agency, and got me a replacement in time. Saved the whole family vacation.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "mke-1",
      name: "Milwaukee Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "341 W St Paul Ave",
      city: "Milwaukee",
      state: "WI",
      zip: "53203",
      phone: "(414) 270-2308",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown acceptance facility. Appointment required for passport applications. Call ahead to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-2",
      name: "Wauwatosa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7600 W State St",
      city: "Wauwatosa",
      state: "WI",
      zip: "53213",
      phone: "(414) 453-2290",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "8 miles west of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Wauwatosa and Brookfield residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-3",
      name: "Milwaukee County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "901 N 9th St, Room 105",
      city: "Milwaukee",
      state: "WI",
      zip: "53233",
      phone: "(414) 278-4064",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "1 mile from downtown Milwaukee",
      website: "https://county.milwaukee.gov/CountyClerk",
      websiteLabel: "Milwaukee County Clerk",
      notes: "Accepts walk-in passport applications. Photo services not available on-site. Bring your own photos.",
      icon: "ri-government-line",
    },
    {
      id: "mke-4",
      name: "Oak Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8515 S Howell Ave",
      city: "Oak Creek",
      state: "WI",
      zip: "53154",
      phone: "(414) 762-5543",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "12 miles south of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Oak Creek, Cudahy, and South Milwaukee residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-5",
      name: "Brookfield City Hall",
      type: "City Clerk Passport Acceptance",
      address: "2000 N Calhoun Rd",
      city: "Brookfield",
      state: "WI",
      zip: "53005",
      phone: "(262) 796-6600",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "13 miles west of downtown Milwaukee",
      website: "https://www.ci.brookfield.wi.us/",
      websiteLabel: "City of Brookfield",
      notes: "Acceptance facility for Brookfield and Waukesha County residents. Photo services may be available nearby.",
      icon: "ri-government-line",
    },
    {
      id: "mke-6",
      name: "Waukesha Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 W Broadway",
      city: "Waukesha",
      state: "WI",
      zip: "53186",
      phone: "(262) 547-2300",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "20 miles west of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Full passport acceptance services for Waukesha County residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited", fpc: "No — we handle everything for you" },
    { feature: "Drive to Chicago", agency: "Yes — 90 miles each way", fpc: "No — we courier for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Driving to Chicago Without Appointment",
      description: "The Chicago Passport Agency requires appointments for all visits — walk-ins are turned away. Fast Passport Center pre-secures your submission slot so you never make a wasted trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Passport photos must be exactly 2×2 inches with a white or off-white background. CVS and Walgreens in Milwaukee are reliable but always verify before submission.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 or DS-82 Form",
      description: "Missing signatures or selecting the wrong form type are the #1 reasons for rejection at Milwaukee County acceptance facilities.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without a confirmed flight itinerary, the Chicago Passport Agency cannot process emergency requests. Always have your flight booking ready.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Both Parents for Child Passport",
      description: "For minors, both parents must appear at the acceptance facility or submit notarized consent. Milwaukee families often miss this requirement.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming Standard Processing is Fast Enough",
      description: "Standard processing takes 8–11 weeks. Milwaukee travelers departing within 10 weeks must use expedited or courier service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop your documents at any UPS location in Milwaukee." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to Chicago Passport Agency." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency using our registered courier submission." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Milwaukee address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "Does Milwaukee or Wisconsin have a local passport agency?",
      a: "No. Wisconsin does not have a regional passport agency. The nearest regional passport agency for Milwaukee and all Wisconsin residents is the Chicago Passport Agency, located at 230 S Dearborn St, Suite 2500, Chicago, IL 60604 — approximately 90 miles from Milwaukee. Fast Passport Center's registered couriers deliver directly to Chicago so you don't have to make the drive.",
    },
    {
      q: "How fast can I get an emergency passport in Milwaukee?",
      a: "Fast Passport Center can get Milwaukee travelers a passport in as little as 24–48 hours through our registered courier service to the Chicago Passport Agency. This is significantly faster than driving to Chicago yourself without a pre-secured appointment or submitting through standard USPS (8–11 weeks).",
    },
    {
      q: "Can I walk into the Chicago Passport Agency from Milwaukee?",
      a: "No. The Chicago Passport Agency requires appointments for all visits — walk-ins are turned away without exception. Our couriers have pre-scheduled submission slots, which is why we can guarantee turnaround times that individuals cannot achieve on their own.",
    },
    {
      q: "What is the fastest way to renew my passport in Milwaukee?",
      a: "The fastest way is through Fast Passport Center's expedited courier service. We courier your documents to the Chicago Passport Agency, achieving 24–48 hour turnaround for emergency cases and 2–9 business days for standard expedited renewal. This is far faster than mailing to the National Passport Processing Center (3–5 weeks) or standard processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Milwaukee?",
      a: "CVS, Walgreens, FedEx Office, and UPS Store locations throughout Milwaukee and southeastern Wisconsin offer passport photos. The CVS on N Oakland Ave and Walgreens on W Capitol Dr are popular options. Always verify your photos meet the 2×2 inch white background requirement before submission.",
    },
    {
      q: "Where can I get my passport application accepted in Milwaukee?",
      a: "Milwaukee County Clerk (901 N 9th St), Milwaukee Main Post Office (341 W St Paul Ave), Wauwatosa Post Office, and Oak Creek Post Office all accept passport applications. Appointments are required at all locations. Fast Passport Center helps you find the most convenient facility and can handle renewal submissions without an in-person visit.",
    },
    {
      q: "Can I take a cruise from Chicago if my Milwaukee passport expires soon?",
      a: "You must have a valid passport for any international cruise. Cruises from Chicago's Navy Pier or other Lake Michigan ports to Canada, Mexico, or the Caribbean all require a valid U.S. passport. Do not book a cruise without verifying your passport expiration date and planned departure.",
    },
    {
      q: "How long does a standard passport renewal take from Milwaukee?",
      a: "Standard USPS mail processing for passport renewal currently takes 8–11 weeks from Milwaukee. Government expedited mail service takes 3–5 weeks. Fast Passport Center's courier service achieves 2–9 business days for expedited and 24–48 hours for emergency cases.",
    },
    {
      q: "What do I need for a first-time passport in Milwaukee?",
      a: "You'll need Form DS-11, a certified birth certificate, a government-issued photo ID (Wisconsin driver's license or ID card), two identical passport photos, and payment. First-time applicants must appear in person at an acceptance facility. We provide a complete checklist and review your documents before your clerk appointment.",
    },
    {
      q: "Can Fast Passport Center help with a lost passport in Milwaukee?",
      a: "Yes. If your passport is lost or stolen, you must complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with a new DS-11 application. Fast Passport Center guides you through the DS-64 process and couriers your replacement application to the Chicago Passport Agency for the fastest possible turnaround.",
    },
    {
      q: "Do I need a passport for flights from Milwaukee Mitchell Airport?",
      a: "A U.S. passport is required for all international flights from Milwaukee Mitchell International Airport (MKE). MKE offers seasonal international service, and a valid passport is required for all foreign destinations. Domestic flights do not require a passport but will require REAL ID from May 2025.",
    },
    {
      q: "What is the cost of expedited passport service from Milwaukee?",
      a: "Government fees are $130 for a passport book plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 2–9 business day expedited, or standard options. Contact us for a quote based on your Milwaukee location and timeline.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — N Oakland Ave", address: "2520 N Oakland Ave, Milwaukee, WI 53211", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — W Capitol Dr", address: "2701 W Capitol Dr, Milwaukee, WI 53206", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — N Water St", address: "770 N Water St, Milwaukee, WI 53202", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — N Mayfair Rd", address: "11616 W Burleigh St, Wauwatosa, WI 53222", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — S 27th St", address: "6201 S 27th St, Franklin, WI 53132", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS — Brookfield Rd", address: "17145 W Bluemound Rd, Brookfield, WI 53005", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Milwaukee South", address: "7131 W Howard Ave, Milwaukee, WI 53220", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals. Appointment recommended.", featured: true },
    { name: "WI DMV — Wauwatosa", address: "11711 W North Ave, Wauwatosa, WI 53226", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Convenient for west side Milwaukee and Waukesha County residents.", featured: false },
    { name: "WI DMV — Oak Creek", address: "9000 S Howell Ave, Oak Creek, WI 53154", hours: "Mon–Fri: 8:30AM–4:30PM", note: "South Milwaukee area DMV. Appointment required for REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "6 miles from downtown Milwaukee",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Milwaukee's primary airport. Offers seasonal international flights. Most international destinations connect through Chicago O'Hare (ORD) or Midway (MDW).",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Flight Info",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "90 miles south of Milwaukee",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub used by many Milwaukee travelers for transatlantic and transpacific flights. Passport required for all international departures.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "80 miles south of Milwaukee",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Alternative Chicago airport for Milwaukee travelers. Some international charter flights. Closer to Milwaukee than O'Hare.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "Midway Airport",
      featured: false,
    },
  ],
};